import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { fetchCoinData } from '../utils/coinApi';
import { CoinBalance, useCoinStore, logoutClearAllRewardStores } from './coinstore';
import { getCurrentServerTime } from '../utils/serverTime';


const TEST_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours (86400000ms)
const TEST_EXPIRY_MS = 48 * 60 * 60 * 1000;   // 48 hours (172800000ms)
// const TEST_COOLDOWN_MS = 10000; // 24 hours (86400000ms)
// const TEST_EXPIRY_MS = 20000;   // 48 hours (172800000ms)

const addMs = (dateStr: string, ms: number) => {
    const base = dateStr ? new Date(dateStr) : new Date();
    return new Date(base.getTime() + ms).toISOString();
};

interface User {
    user_id: string,
    user_email: string,
    user_email_auth: string,
    user_name: string,
    user_image: string,
    offer: string,
    offer_packname: string,
    password_status: string,
    is_update: string,
    device_limit: string,
    device_list: string[],
    premiumpurchase: string,
    user_dob: string,
    user_gender: string,
    premium_data: {
        expiry: string,
        originalTX: string,
        plan_type: string,
        premium: string,
        priceAmountMicros: string,
        priceCurrencyCode: string,
        product_id: string,
        purchase_token: string,
        source: string
    }
}

interface AuthState {
    user: User | null;
    login: (userData: User) => void;
    logout: () => void;
}

export const useAuthStore = create<AuthState>()(
    persist<AuthState>(
        (set) => ({
            user: null,
            login: async (userData: User) => {
                // Don't process coin data if user_id is invalid or empty
                if (!userData.user_id || userData.user_id === '0') {
                    console.warn('Login called with invalid user_id:', userData.user_id);
                    return;
                }
                
                const coinData = await fetchCoinData(userData.user_id, userData.user_email).catch(() => { /* ignore errors */ });
                const coins = useCoinStore.getState();
                
                if (coinData) {
                    // Skip rehydration during login to preserve fresh API data
                    coins.setSkipRehydrate(true);
                    
                    const d = coinData[0].data;
                    const dayStreakNum = Number(d.day_streak || 0);
                    const computedWeekNumber = dayStreakNum > 0 ? Math.floor((dayStreakNum - 1) / 7) + 1 : 1;
                    const lastCheckin = d.mobile_dt || '';
                    // Only calculate nextEligible and streakExpiry if user has an ACTIVE streak (day_streak > 0)
                    // Fresh accounts have day_streak = 0, so they shouldn't see a timer
                    const nextEligible = dayStreakNum > 0 ? addMs(lastCheckin, TEST_COOLDOWN_MS) : '';
                    const streakExpiry = dayStreakNum > 0 ? addMs(lastCheckin, TEST_EXPIRY_MS) : '';
                    // console.log('AuthStore Checkin Debug:', { d_mobile_dt: d.mobile_dt, dayStreakNum, nextEligible, streakExpiry, isFresh: dayStreakNum === 0 });
                    // Get current server time for internal use (store as ISO internally for consistency)
                    const currentServerTime = getCurrentServerTime().toISOString();
                    // Use server time for date (not system time)
                    const today = currentServerTime.split('T')[0];
                    
                    // Only reset watchDuration if date actually changed (based on server time)
                    // Compare watchDurationDate (old day) with today's server date
                    const dateChanged = coins.coinBalance.watchDurationDate && 
                                       coins.coinBalance.watchDurationDate !== today;
                    
                    const obj: CoinBalance = {
                        totalCoins: Number(d.coins),
                        streakDays: d.day_streak,
                        weekNumber: computedWeekNumber,
                        turn_on_web_notification: d.turn_on_web_notification,
                        bindEmail: d.bind_email,
                        // Map backend 0/1 to local 0/2 for claimed; preserve local "1" if user had visited
                        yt: d.subscribe_youtube==="1" ? '2' : coins.coinBalance.yt,
                        fb: (d.share_facebook==="1" || d.follow_facebook==="1") ? '2' : coins.coinBalance.fb,
                        insta: (d.share_instagram==="1" || d.follow_instagram==="1") ? '2' : coins.coinBalance.insta,
                        frindShare: d.share_with_friends,
                        // Use API response for claims
                        is10: d.is_10==="1" ? '2' : coins.coinBalance.is10,
                        is20: d.is_20==="1" ? '2' : coins.coinBalance.is20,
                        welcomeBonus: d.welcome_bonus,
                        mobileDt: d.mobile_dt,
                        episodes: d.episode_ids, // Sync unlocked episode IDs from backend
                        // Reset watch duration only when date actually changes (not just when is_10/is_20 are "0")
                        watchDuration: dateChanged ? 0 : coins.coinBalance.watchDuration,
                        watchDurationDate: coins.coinBalance.watchDurationDate,
                        lastCheckinTime: lastCheckin,
                        nextEligibleTime: nextEligible,
                        streakExpiryTime: streakExpiry,
                        serverTime: currentServerTime,
                    }
                    // console.log("Obj coin replace", obj)
                    coins.replaceCoinBalance(obj);
                    
                    // Re-enable rehydration after setting fresh data
                    coins.setSkipRehydrate(false);
                    // console.log('coindata: ', coinData[0].data)
                }

                if (!userData.user_email || !userData.user_id) return;
                const correctedUserData = { ...userData };

                if (correctedUserData.premium_data &&
                    correctedUserData.premium_data.premium === "1" &&
                    correctedUserData.premium_data.plan_type &&
                    correctedUserData.premium_data.plan_type !== "") {

                    const planType = correctedUserData.premium_data.plan_type;

                    if (!planType.toLowerCase().includes("plus") && !planType.toLowerCase().includes("elite")) {
                        correctedUserData.premium_data.plan_type = planType + " Elite";
                    }
                }

                set({ user: correctedUserData })
            },
            logout: () => {
                // Clear user
                set({ user: null });
                try { localStorage.removeItem('_user'); } catch {}
                // Aggressively clear all reward-related persisted stores
                try { logoutClearAllRewardStores(); } catch {}
            },
        }), { name: '_user' }
    )
);



